import type { FC } from 'react'
import { useMemo } from 'react'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import type { DehydratedState } from '@tanstack/react-query'
import { QueryClient, dehydrate } from '@tanstack/react-query'

import { CustomersView } from 'views/Customers'

import type { CustomersViewProps } from 'views/Customers/LandingPage/Customers'

import {
  createFetchCustomersThumbnailsQuery,
  fetchFeaturedCustomers,
  fetchFilters,
  fetchMetaData,
} from '@segment/api/routes/customers/queries'

import { createClient } from '@segment/api/staticSiteBackend/client'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

const Customers: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ filters, heroData, metaData }) => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])
  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <CustomersView filters={filters} heroData={heroData} metaData={metaData} />
    </StaticSiteBackendProvider>
  )
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & CustomersViewProps> = async () => {
  const queryClient = new QueryClient()
  const client = createClient()
  const filters = await fetchFilters(client)
  const heroData = await fetchFeaturedCustomers(client)
  const metaData = await fetchMetaData(client)

  await queryClient.prefetchInfiniteQuery(
    ...createFetchCustomersThumbnailsQuery(client, { filters: { useCase: [], industry: [], region: [], products: [] } })
  )

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props = {
    dehydratedState: safeDehydratedState,
    filters,
    heroData,
    metaData,
  }

  return {
    props,
  }
}

export default Customers
